import {
  Box,
  Container,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
} from '@chakra-ui/react';
import { FC, memo, useEffect } from 'react';

import { layoutStyled } from '@components/Layout/Layout.styles';
import { ILayout } from '@components/Layout/Layout.types';
import LockScreen from '@components/LockScreen';
import Navbar from '@components/Navbar';
import RenderIf from '@components/RenderIf';
import SEO from '@components/SeoComponent/Seo';
import Sidebar from '@components/Sidebar';
import { appTheme, isLockedState, sidebarIsOpen } from '@store/Common';
import { useRouter } from 'next/router';
import { useRecoilValue, useSetRecoilState } from 'recoil';

const Layout: FC<ILayout> = ({
  children,
  title,
  description,
  imageUrl,
  urlCanonical,
  menuSelected,
  isNavbar = true,
  isSidebar = true,
  isNoShadow,
  isFullWidth = false,
  isAuthLayout,
  ...props
}): JSX.Element => {
  const layoutType = useRecoilValue(appTheme);
  const locked = useRecoilValue(isLockedState);
  const setLocked = useSetRecoilState(isLockedState);
  const isOpenSidebar = useRecoilValue(sidebarIsOpen);
  const setSidebarOpen = useSetRecoilState(sidebarIsOpen);
  const router = useRouter();

  useEffect(() => {
    setSidebarOpen(false);
  }, [router]);

  return (
    <>
      <SEO
        title={title}
        description={description}
        imageUrl={imageUrl}
        urlCanonical={urlCanonical}
        type="website"
      />
      {locked && <LockScreen onUnlock={() => setLocked(false)} />}
      <Box bg="gray.50" minH="100vh" display="flex" w="100vw" {...props}>
        <Box
          minW="280px"
          {...(layoutType !== 'sidebar' || isAuthLayout
            ? { display: 'none' }
            : { display: 'block' })}
        >
          <RenderIf
            isTrue={isSidebar && layoutType === 'sidebar' && !isAuthLayout}
          >
            <Sidebar menuSelected={menuSelected} />
          </RenderIf>
        </Box>
        <Drawer
          isOpen={isOpenSidebar}
          placement="left"
          onClose={() => setSidebarOpen(false)}
        >
          <DrawerOverlay />
          <DrawerContent p="0" maxW="280px">
            <DrawerBody p="0" overflow="hidden">
              <Sidebar isMobile menuSelected={menuSelected} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        <Box {...layoutStyled()}>
          <RenderIf isTrue={isNavbar && !isAuthLayout}>
            <Navbar isNoShadow={isNoShadow} />
          </RenderIf>
          <Container
            as="main"
            id="container"
            maxW="1920px"
            px={{
              base: '4',
              lg: '8',
            }}
            pt="4"
            pb="8"
            {...(isFullWidth && { maxW: 'unset', p: '0 !important' })}
          >
            {children}
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default memo(Layout);
